
import React, { useState, useEffect, useMemo } from "react";
import { useTournament } from "@/contexts/TournamentContext";
import { useLanguage } from "@/contexts/LanguageContext";
import { Button } from "@/components/ui/button";
import { SharedResultsEntry } from "./shared/SharedResultsEntry";
import { Leaderboard } from "./mexicano/Leaderboard";
import { BenchDisplay } from "./mexicano/BenchDisplay";
import { Trophy, Users, ClipboardList, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { Player } from "@/types/tournament";
import { MexicanoAdapter } from "@/adapters/tournament-adapters";

interface MexicanoTournamentProps {
  participants: any[];
  onBack?: () => void;
}

export const MexicanoTournament: React.FC<MexicanoTournamentProps> = ({ participants, onBack }) => {
  const { t, language } = useLanguage();
  const { 
    tournamentState, 
    startTournament, 
    resetTournament,
    showLeaderboard,
    setShowLeaderboard,
    goToPreviousRound,
    submitRoundResults
  } = useTournament();
  const [rounds, setRounds] = useState(3);

  // Create adapter for shared component using useMemo to ensure it updates when tournament state changes
  const mexicanoAdapter = useMemo(() => 
    new MexicanoAdapter(tournamentState, submitRoundResults), 
    [tournamentState, submitRoundResults]
  );

  const handleBack = () => {
    // If we're in round 1, go back to front page
    if (tournamentState.currentRound === 1) {
      if (onBack) {
        onBack();
      } else {
        resetTournament();
      }
    } else {
      // If we're in round 2+, go to previous round
      if (goToPreviousRound) {
        goToPreviousRound();
      }
    }
  };

  // Auto-start tournament when component mounts (when tournament type is selected)
  useEffect(() => {
    if (!tournamentState.isActive && tournamentState.currentRound === 0) {
      handleStartTournament();
    }
  }, []); // Empty dependency array means this runs once when component mounts

  const handleStartTournament = () => {
    if (participants.length < 12 || participants.length > 16) {
      toast.error(language === 'no' 
        ? `Trenger 12-16 spillere (${participants.length} valgt)`
        : `Need 12-16 players (${participants.length} selected)`);
      return;
    }

    const players: Player[] = participants.map((p, index) => ({
      id: p.mobil || String(index),
      name: `${p.fornavn} ${p.etternavn.charAt(0)}.`,
      phone: p.mobil,
      points: 0,
      matchesPlayed: 0,
      isOnBench: false,
    }));

    startTournament(players, rounds);
  };

  let content;
  
  if (tournamentState.isActive || (!tournamentState.isActive && tournamentState.currentRound > 0)) {
    content = (
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
          <div className="flex items-center gap-2">
            {!showLeaderboard && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleBack}
                className="text-xs sm:text-sm"
              >
                <ArrowLeft className="h-3 w-3 sm:h-4 sm:w-4" />
                <span className="hidden sm:inline ml-1">
                  {language === 'no' ? 'Tilbake' : 'Back'}
                </span>
              </Button>
            )}
            <h2 className="font-bold text-lg sm:text-xl flex items-center gap-2">
              <Trophy className="text-primary flex-shrink-0" />
              <span className="truncate">
                {tournamentState.isActive 
                  ? (language === 'no' ? `Mexicano Runde ${tournamentState.currentRound}` : `Mexicano Round ${tournamentState.currentRound}`)
                  : (language === 'no' ? 'Mexicano Turnering Ferdig' : 'Mexicano Tournament Finished')
                }
              </span>
            </h2>
          </div>
          
          <div className="flex gap-2 flex-shrink-0">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowLeaderboard(!showLeaderboard)}
              className="text-xs sm:text-sm min-w-0"
            >
              <ClipboardList className="mr-1 sm:mr-2 h-3 w-3 sm:h-4 sm:w-4 flex-shrink-0" />
              <span className="whitespace-nowrap">
                {showLeaderboard 
                  ? (language === 'no' ? 'Skjul resultater' : 'Hide results')
                  : (language === 'no' ? 'Vis resultater' : 'Show results')}
              </span>
            </Button>
          </div>
        </div>

        {/* Show bench players if any */}
        {tournamentState.players.some(p => p.isOnBench) && (
          <BenchDisplay />
        )}
        
        {showLeaderboard ? (
          <Leaderboard />
        ) : tournamentState.isActive ? (
          <SharedResultsEntry 
            adapter={mexicanoAdapter}
            currentRound={tournamentState.currentRound}
            totalRounds={tournamentState.totalRounds}
          />
        ) : (
          <Leaderboard />
        )}
      </div>
    );
  }

  return (
    <div className="mt-8 border-t pt-6">
      {content}
    </div>
  );
};
